/* eslint-disable import/no-anonymous-default-export */
import {
    Box,
    Button as MuiButton,
    Card,
    Stack,
    Typography,
} from '@mui/material';
import { Plus, Users } from 'lucide-react';
import jsonExport from 'jsonexport/dist';
import type { Exporter } from 'react-admin';
import {
    downloadCSV,
    ListBase,
    ListToolbar,
    Pagination,
    SearchInput,
    Title,
    TopToolbar,
    useGetIdentity,
    useListContext,
} from 'react-admin';
import { useState } from 'react';

import { Account, Contact, Sale, Tag } from '../types';
import { ContactEmpty } from './ContactEmpty';
import { ContactCardGrid } from './ContactCardGrid';
import { ContactCreateDialog } from './ContactCreateDialog';

const contactFilters = [
    <SearchInput
        key="q"
        source="q"
        alwaysOn
        placeholder="Search contacts..."
    />,
];

const ContactListActions = ({
    onCreateClick,
}: {
    onCreateClick: () => void;
}) => (
    <TopToolbar>
        <MuiButton
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={onCreateClick}
            sx={{
                backgroundColor: '#10b981',
                '&:hover': {
                    backgroundColor: '#059669',
                },
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.8125rem',
            }}
        >
            New Contact
        </MuiButton>
    </TopToolbar>
);

export const ContactList = () => {
    const { identity } = useGetIdentity();
    if (!identity) return null;
    return (
        <ListBase
            perPage={25}
            sort={{ field: 'last_name', order: 'ASC' }}
            exporter={exporter}
        >
            <ContactLayout />
        </ListBase>
    );
};

const ContactLayout = () => {
    const { data, isPending, filterValues } = useListContext();
    const hasSearch = filterValues?.q?.trim?.();
    const [dialogOpen, setDialogOpen] = useState(false);

    if (isPending) return null;
    if (!data?.length && !hasSearch) return <ContactEmpty />;

    return (
        <Stack component="div" sx={{ width: '100%' }}>
            <Title title="Contacts" />
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Users size={24} color="#3b82f6" />
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: '#0f172a',
                    }}
                >
                    Contacts
                </Typography>
            </Box>
            <ListToolbar
                filters={contactFilters}
                actions={
                    <ContactListActions
                        onCreateClick={() => setDialogOpen(true)}
                    />
                }
            />

            <Card
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                    p: 2,
                }}
            >
                <ContactCardGrid />
            </Card>
            <Box mt={1}>
                <Pagination rowsPerPageOptions={[12, 24, 48]} />
            </Box>
            <ContactCreateDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            />
        </Stack>
    );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
    const accounts = await fetchRelatedRecords<Account>(
        records,
        'account_id',
        'accounts'
    );
    const sales = await fetchRelatedRecords<Sale>(records, 'sales_id', 'sales');
    const tags = await fetchRelatedRecords<Tag>(records, 'tags', 'tags');

    const contacts = records.map(contact => {
        const exportedContact = {
            ...contact,
            account:
                contact.account_id != null
                    ? accounts[contact.account_id].name
                    : undefined,
            sales: `${sales[contact.sales_id].first_name} ${sales[contact.sales_id].last_name}`,
            tags: contact.tags.map(tagId => tags[tagId].name).join(', '),
            email_work: contact.email_jsonb?.find(
                email => email.type === 'Work'
            )?.email,
            email_home: contact.email_jsonb?.find(
                email => email.type === 'Home'
            )?.email,
            email_other: contact.email_jsonb?.find(
                email => email.type === 'Other'
            )?.email,
            email_jsonb: JSON.stringify(contact.email_jsonb),
            email_fts: undefined,
            phone_work: contact.phone_jsonb?.find(
                phone => phone.type === 'Work'
            )?.number,
            phone_home: contact.phone_jsonb?.find(
                phone => phone.type === 'Home'
            )?.number,
            phone_other: contact.phone_jsonb?.find(
                phone => phone.type === 'Other'
            )?.number,
            phone_jsonb: JSON.stringify(contact.phone_jsonb),
            phone_fts: undefined,
        };
        delete exportedContact.email_fts;
        delete exportedContact.phone_fts;
        return exportedContact;
    });
    return jsonExport(contacts, {}, (_err: any, csv: string) => {
        downloadCSV(csv, 'contacts');
    });
};
